import { useState, useMemo } from 'react'
import { Search } from 'lucide-react'
import { useOnboarding } from '../../contexts/OnboardingContext'
import { INDUSTRY_CLUSTERS } from '../../config/industries'

export interface IndustryStepProps {
  onNext: () => void
  onBack: () => void
}

/**
 * IndustryStep displays 101 industries grouped into 12 clusters.
 * Users can search/filter industries and select exactly one.
 * The "Next" button is enabled only when an industry is selected.
 */
export function IndustryStep({ onNext, onBack }: IndustryStepProps) {
  const { state, dispatch } = useOnboarding()
  const [searchQuery, setSearchQuery] = useState(state.industrySearchQuery || '')
  const [validationError, setValidationError] = useState<string | null>(null)

  const selectedIndustryId = state.selectedIndustry
  const selectedClusterId = state.selectedIndustryCluster

  // Filter clusters and industries based on search query
  const filteredClusters = useMemo(() => {
    if (!searchQuery.trim()) {
      return INDUSTRY_CLUSTERS
    }

    const query = searchQuery.toLowerCase()

    return INDUSTRY_CLUSTERS.map((cluster) => ({
      ...cluster,
      industries: cluster.industries.filter((industry) =>
        industry.name.toLowerCase().includes(query)
      ),
    })).filter((cluster) => cluster.industries.length > 0)
  }, [searchQuery])

  const hasResults = filteredClusters.length > 0

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setValidationError(null)
  }

  const handleIndustrySelect = (clusterId: string, industryId: string) => {
    dispatch({
      type: 'SET_INDUSTRY',
      payload: {
        cluster: clusterId,
        industry: industryId,
        searchQuery,
      },
    })
    setValidationError(null)
  }

  const handleNext = () => {
    if (!selectedIndustryId) {
      setValidationError('Please select an industry to continue.')
      return
    }
    onNext()
  }

  return (
    <div className="w-full max-w-3xl mx-auto">
      <h2 className="text-2xl font-semibold text-secondary-900 mb-2">
        Select Your Industry
      </h2>
      <p className="text-secondary-600 mb-6">
        Choose the industry that best describes your business.
      </p>

      {/* Search input */}
      <div className="relative mb-6">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400"
          aria-hidden="true"
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search industries..."
          aria-label="Search industries"
          className="w-full pl-10 pr-4 py-2.5 border border-secondary-300 rounded-lg text-secondary-900 placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
        />
      </div>

      {/* Industry clusters list */}
      <div className="max-h-96 overflow-y-auto border border-secondary-200 rounded-lg p-4 mb-6">
        {hasResults ? (
          filteredClusters.map((cluster) => (
            <div key={cluster.id} className="mb-4 last:mb-0">
              <h3 className="text-sm font-semibold text-secondary-700 uppercase tracking-wide mb-2">
                {cluster.name}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {cluster.industries.map((industry) => {
                  const isSelected =
                    selectedIndustryId === industry.id &&
                    selectedClusterId === cluster.id

                  return (
                    <button
                      key={industry.id}
                      type="button"
                      onClick={() => handleIndustrySelect(cluster.id, industry.id)}
                      aria-pressed={isSelected}
                      className={`
                        text-left px-3 py-2 rounded-md border text-sm transition-colors
                        ${
                          isSelected
                            ? 'bg-primary-50 border-primary-500 text-primary-800 font-medium ring-1 ring-primary-500'
                            : 'bg-white border-secondary-200 text-secondary-700 hover:bg-secondary-50 hover:border-secondary-300'
                        }
                      `}
                    >
                      {industry.name}
                    </button>
                  )
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-secondary-500">
            <Search className="w-8 h-8 mb-2 text-secondary-300" aria-hidden="true" />
            <p className="text-sm">No results found</p>
            <p className="text-xs text-secondary-400 mt-1">
              Try a different search term
            </p>
          </div>
        )}
      </div>

      {/* Validation error */}
      {validationError && (
        <p className="text-sm text-danger-600 mb-4" role="alert">
          {validationError}
        </p>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2.5 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          aria-disabled={!selectedIndustryId}
          className={`
            px-6 py-2.5 text-sm font-medium rounded-lg transition-colors
            ${
              selectedIndustryId
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
            }
          `}
        >
          Next
        </button>
      </div>
    </div>
  )
}
